import { useCallback, useRef } from "react"

/**
 * Pointer travel (px) above which a click is treated as a drag-select, not a
 * click. Small enough that a normal click (a few sub-pixel jitters) still
 * toggles, large enough that any deliberate text selection is caught.
 */
const DRAG_THRESHOLD_PX = 6

/**
 * Handlers for a click target that should toggle on a genuine click but stay
 * put while the user is selecting text (so text on the target stays copyable).
 *
 * A click is suppressed when either:
 *  - the pointer moved past {@link DRAG_THRESHOLD_PX} between mousedown and
 *    mouseup — i.e. the user drag-selected, wherever the drag started or ended;
 *  - a live text selection exists at click time — covers double-click-to-select
 *    a word and shift-click selection extension, which involve no drag.
 *
 * A plain click anywhere on the target — including directly on its text — still
 * toggles, so "click anywhere to expand" is preserved. Spread the returned
 * handlers onto the toggle element; wire keyboard activation separately, since
 * keyboard toggles never need guarding.
 */
export function useToggleWithoutSelection(onToggle?: () => void): {
  onMouseDown: (e: React.MouseEvent) => void
  onClick: (e: React.MouseEvent) => void
} {
  const downRef = useRef<{ x: number; y: number } | null>(null)

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    downRef.current = { x: e.clientX, y: e.clientY }
  }, [])

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      const down = downRef.current
      downRef.current = null

      if (
        down != null &&
        (Math.abs(e.clientX - down.x) > DRAG_THRESHOLD_PX ||
          Math.abs(e.clientY - down.y) > DRAG_THRESHOLD_PX)
      ) {
        return
      }

      if ((window.getSelection()?.toString().length ?? 0) > 0) {
        return
      }

      onToggle?.()
    },
    [onToggle],
  )

  return { onMouseDown, onClick }
}
